import { FC } from 'react';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import dashboardMultiActionsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_button_click';
import dashboardMultiActionsStopModalShown from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_stop_modal_shown';
import { IconColor, IconScale } from '@hh.ru/bloko/build/blocks/icon';
import Loading from '@hh.ru/bloko/build/blocks/loading';
import Text from '@hh.ru/bloko/build/blocks/text';
import { formatToReactComponent } from '@hh.ru/bloko/build/common/trl';
import { HSpacing, VSpacing } from '@hh.ru/magritte-ui';

import Button, { ButtonKind } from 'bloko/blocks/button';
import Conversion from 'bloko/blocks/conversion';
import Modal, { ModalFooter, ModalTitle } from 'bloko/blocks/modal';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { fetchMultiStop } from 'components/Dashboard/MultiActions/fetchMultiStop';

import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/Dashboard/MultiActions/MultiStopConfirmationModal/MultiStopConfirmationModal.module.less';

interface MultiStopConfirmationModalProps {
    isVisible: boolean;
    hideModal: () => void;
}

const TrlKeys = {
    title: 'Dashboard.MultiStopConfirmationModal.title',
    campaignsCount: {
        zero: 'campaign.zero',
        one: 'campaign.one',
        some: 'campaign.some',
        many: 'campaign.many',
    },
    text: 'Dashboard.MultiStopConfirmationModal.text',
    cancelButton: 'cancel',
    stopButton: 'Dashboard.MultiStopConfirmationModal.stopButton',
    loadingText: 'Dashboard.MultiStopConfirmationModal.loadingText',
};

const MultiStopConfirmationModal: FC<MultiStopConfirmationModalProps> = ({ isVisible, hideModal }) => {
    const [isLoading, setIsLoadingOn, setIsLoadingOff] = useOnOffState(false);
    const checkedCampaignIds = useSelector((state) => state.dashboard.checkedCampaignIds);
    const checkedCampaignsCount = Object.keys(checkedCampaignIds).length;

    const trl = useTrls();
    const dispatch = useDispatch();

    const onStopConfirm = () => {
        setIsLoadingOn();
        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_stop_confirm',
            checkedCampaignsCount,
            campaignIds: JSON.stringify(Object.keys(checkedCampaignIds).map(Number)),
        });
        void dispatch(fetchMultiStop(checkedCampaignIds)).finally(() => {
            setIsLoadingOff();
            hideModal();
        });
    };

    return (
        <ElementShownAnchor fn={dashboardMultiActionsStopModalShown}>
            <Modal visible={isVisible} onClose={hideModal} backgroundClick={!isLoading} closeButtonShow={!isLoading}>
                {isLoading ? (
                    <div className={styles.loader}>
                        <VSpacing default={32} />
                        <Loading initial={IconColor.Gray50} scale={IconScale.Medium} />
                        <VSpacing default={16} />
                        <Text>{trl(TrlKeys.loadingText)}</Text>
                        <VSpacing default={32} />
                    </div>
                ) : (
                    <>
                        <ModalTitle>
                            {formatToReactComponent(trl(TrlKeys.title), {
                                '[0]': (
                                    <Conversion
                                        zero={trl(TrlKeys.campaignsCount.zero)}
                                        one={trl(TrlKeys.campaignsCount.one)}
                                        some={trl(TrlKeys.campaignsCount.some)}
                                        many={trl(TrlKeys.campaignsCount.many)}
                                        value={checkedCampaignsCount}
                                        hasValue={true}
                                    />
                                ),
                            })}
                        </ModalTitle>
                        <div className={styles.contentWrapper}>
                            <VSpacing default={8} />
                            <Text>{trl(TrlKeys.text)}</Text>
                        </div>
                        <ModalFooter>
                            <div className={styles.footer}>
                                <Button onClick={hideModal} stretched data-qa="confirmation-cancel">
                                    {trl(TrlKeys.cancelButton)}
                                </Button>
                                <HSpacing default={8} />
                                <Button
                                    data-qa="confirmation-create"
                                    kind={ButtonKind.Primary}
                                    onClick={onStopConfirm}
                                    stretched
                                >
                                    {trl(TrlKeys.stopButton)}
                                </Button>
                            </div>
                        </ModalFooter>
                    </>
                )}
            </Modal>
        </ElementShownAnchor>
    );
};

export default MultiStopConfirmationModal;
